import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

export default function AsianAlone3DChart({ data }) {
  const pdfRef = useRef();

  if (!data || data.length === 0) {
    return <p>No data available</p>;
  }

  /* =========================
     SUMMARY CALCULATIONS
  ========================== */

  const firstValue = Number(data[0].asian_alone_total || 0);
  const lastValue = Number(data[data.length - 1].asian_alone_total || 0);

  const growthPercent = (((lastValue - firstValue) / firstValue) * 100).toFixed(
    1,
  );

  const peakYear = data.reduce(
    (max, row) =>
      Number(row.asian_alone_total) > max.value
        ? { year: row.year, value: Number(row.asian_alone_total) }
        : max,
    {
      year: data[0].year,
      value: Number(data[0].asian_alone_total),
    },
  );

  /* =========================
     CHART DATA
  ========================== */

  const chartData = {
    labels: data.map((d) => d.year),
    datasets: [
      {
        label: "Asian Alone Total",
        data: data.map((d) => Number(d.asian_alone_total || 0)),
        backgroundColor: "#dc2626",
        borderRadius: 8,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Asian Alone Population Trend (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return context.parsed.y.toLocaleString("en-US");
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Year" },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Population" },
        ticks: {
          callback: (value) => value.toLocaleString("en-US"),
        },
      },
    },
  };

  /* =========================
     PDF EXPORT
  ========================== */

  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdfDoc = new jsPDF("p", "mm", "a4");

      const pageWidth = pdfDoc.internal.pageSize.getWidth();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdfDoc.addImage(utahData, "PNG", 60, 10, 32, 16);
      pdfDoc.setFontSize(26);
      pdfDoc.text("UTahData.org", 95, 22);
      pdfDoc.line(10, 35, 200, 35);

      pdfDoc.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);

      pdfDoc.line(10, 260, 200, 260);
      pdfDoc.addImage(utahData, "PNG", 22, 265, 30, 15);
      pdfDoc.setFontSize(10);
      pdfDoc.text("Built by", 55, 275);
      pdfDoc.addImage(bodex, "PNG", 68, 269, 44, 10);
      pdfDoc.text("using", 112, 275);
      pdfDoc.addImage(kpi, "PNG", 125, 265, 20, 20);
      pdfDoc.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        150,
        295,
      );
      pdfDoc.text(`From: ${window.location.href}`, 10, 295);

      pdfDoc.save("asian_population_trend.pdf");
    });
  };

  /* =========================
     UI
  ========================== */

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Asian Alone Population Trend (2018–2024)
        </h2>

        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Overall Growth</p>
            <p className="text-xl font-bold text-blue-800">+{growthPercent}%</p>
          </div>

          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Peak Year</p>
            <p className="text-xl font-bold text-yellow-800">{peakYear.year}</p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">2024 Population</p>
            <p className="text-xl font-bold text-green-800">
              {lastValue.toLocaleString("en-US")}
            </p>
          </div>
        </div>

        {/* TABLE + CHART */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* TABLE */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Year</th>
                  <th className="p-2">Asian Alone Total</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr
                    key={row.year}
                    className={`${
                      row.year === data[data.length - 1].year
                        ? "bg-green-100 font-bold"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <td className="p-2">{row.year}</td>
                    <td className="p-2">
                      {Number(row.asian_alone_total || 0).toLocaleString(
                        "en-US",
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* CHART */}
          <div className="flex flex-col w-full bg-white rounded-lg shadow-md">
            <div className="w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* DOWNLOAD BUTTON */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
